import { useEffect, useState } from 'react';

/**
 * Shared "how fresh is the NetSuite data" store.
 *
 * `useSocket()` opens a connection per caller, and the backend sends the
 * instant-cache reply + "refreshing" signal to the *requesting* socket. So
 * whichever data hook is mounted (operations / work orders / sales orders)
 * stamps this module-level store from its own socket events, and the freshness
 * indicator reads it via `useNetSuiteFreshness`. This avoids opening a second
 * socket that wouldn't receive those per-client events.
 */
let lastUpdatedAt = 0;
let refreshing = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Call when any NetSuite data (cached or fresh) arrives. */
export function markNetSuiteUpdated(): void {
  lastUpdatedAt = Date.now();
  refreshing = false;
  notify();
}

/** Call when the backend signals a background refresh is in flight. */
export function markNetSuiteRefreshing(): void {
  refreshing = true;
  notify();
}

export function useNetSuiteFreshness(): { lastUpdatedAt: number; refreshing: boolean } {
  const [, force] = useState(0);
  useEffect(() => {
    const listener = () => force((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return { lastUpdatedAt, refreshing };
}
